import { useRef } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { IconChevronDown, IconChevronUp, IconSetting } from "@mzc-cloudplex/icons";
import fp from "lodash/fp";
import { useTranslation } from "react-i18next";
import {
    Button,
    Grid,
    Typography,
    Stack,
    Checkbox,
    FormControlLabel,
    Box,
    Popover,
    MenuList,
    MenuItem,
} from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { alpha } from "@mui/material";

const DashboardSettingPopper = ({ open, widgets, selected, onChangeToggle, onClickToggle }) => {
    const { t } = useTranslation();
    const preferenceButtonRef = useRef(null);
    const onHandleChange = (e) => {
        if (open && e.target.closest(".MuiPaper-root")) return;
        onChangeToggle(false);
    };

    return (
        <OutsideClickHandler onOutsideClick={onHandleChange}>
            <Button
                onClick={() => {
                    onChangeToggle(!open);
                }}
                size="small"
                color={open ? "primary" : "greySecondary"}
                startIcon={<IconSetting size={16} />}
                endIcon={open ? <IconChevronUp size={10} /> : <IconChevronDown size={10} />}
                ref={preferenceButtonRef}
            >
                <Typography variant="body1_500">{t(`common::label::Widget Preference`)}</Typography>
            </Button>
            <Popover
                anchorEl={preferenceButtonRef.current}
                open={open && Boolean(preferenceButtonRef.current)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                sx={{
                    ".MuiPaper-root": {
                        width: "420px",
                        p: theme.spacing(2),
                        borderRadius: theme.spacing(0.5),
                        border: `1px solid ${theme.palette.greySecondary[100]}`,
                    },
                }}
            >
                <Stack>
                    <Grid container gap={0.5}>
                        <Typography variant="body1_700">{t(`common::label::Selected`)}</Typography>
                        <Typography color="primary.main" variant="body1_700">
                            {fp.get(`length`, selected)}
                        </Typography>
                        <Typography variant="body1_700"> / {fp.get("length", widgets)}</Typography>
                    </Grid>
                    <MenuList
                        sx={{
                            "& .MuiMenuItem-root": {
                                mb: theme.spacing(0.75),
                                border: `1px solid ${theme.palette.greySecondary[100]}`,
                                borderRadius: theme.spacing(0.25),
                                "&.Mui-selected": {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    backgroundColor: alpha(theme.palette.greySecondary["A100"], 0.24),
                                },
                            },
                        }}
                    >
                        {widgets?.map((widget, i) => (
                            <MenuItem key={fp.get(`name`, widget)} selected={widget.isExposed}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="greySecondary"
                                            checked={widget.isExposed}
                                            onChange={(e) => onClickToggle(widget, e.target.checked)}
                                            disabled={
                                                (fp.isEqual(1, widgets.length) &&
                                                    fp.isEqual(widget, fp.head(widgets))) ||
                                                !widget.isExposeEditable
                                            }
                                            sx={{ mr: `${theme.spacing(0)} !important` }}
                                        />
                                    }
                                    label={
                                        <Grid container alignItems={"center"} gap={1.5}>
                                            <Grid item>
                                                <Box
                                                    display={"flex"}
                                                    alignItems={"center"}
                                                    justifyContent={"center"}
                                                    minWidth={22}
                                                    height={16}
                                                    borderRadius={1}
                                                    sx={{
                                                        backgroundColor:
                                                            widget.color ?? theme.palette.greySecondary[200],
                                                        color: theme.palette.constant.white,
                                                    }}
                                                >
                                                    {fp.get(`icon`, widget)}
                                                </Box>
                                            </Grid>
                                            <Typography variant="body1_700" color={theme.palette.greySecondary.main}>
                                                {t(`common::label::${widget.title}`, widget.title ?? "")}
                                            </Typography>
                                        </Grid>
                                    }
                                    sx={{
                                        width: 1,
                                        height: theme.spacing(6),
                                        m: 0,
                                        pl: 1,
                                        gap: 1,
                                    }}
                                />
                            </MenuItem>
                        ))}
                    </MenuList>
                </Stack>
            </Popover>
        </OutsideClickHandler>
    );
};

export default DashboardSettingPopper;
